/* eslint-disable max-lines-per-function */
import { Permissions, ResourceType, RolePermissionDc } from "@evergis/api";
import { ChipAutoComplete, ChipAutoCompleteData, Description, H3, Icon, useDialogShadow } from "@evergis/ui";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getUsername } from "../../../../ducks/user";

import { useDialogInfo, useDialogReset } from "../../../../hooks/useDialogs";
import { useToggle } from "../../../../hooks/useToggle";
import { updatePermissions } from "../../../../resources/bulkOperations";

import { isConfigure } from "../../../../utils/permissions";
import { DialogActions } from "../../DialogComponents/DialogActions";

import { DialogTitle } from "../../DialogComponents/DialogTitle";
import { FullScreenDialog } from "../../DialogComponents/FullScreenDialog";
import { DialogContent, RestrictedWidthContainer } from "../../DialogComponents/styled";
import { AccessControl } from "../components/AccessControl";
import { CopyPublicResourceLink } from "../components/CopyPublicResourceLink";
import { getAclRole, getNameByRole } from "../getAclRole";
import { useAccessNotifications } from "../hooks/useAccessNotifications";
import { useContext } from "../hooks/useContext";
import { useTotal } from "../hooks/useTotal";
import { CommonSettingsControls, CommonSettingsTitleContainer, StructureMessage } from "../styled";
import { AccessRoleGroup } from "../types";
import { useUsersAndRoles } from "../useUsersAndRoles";
import { useProjectInfo } from "./hooks/useProjectInfo";
import { useStructure } from "./hooks/useStructure";
import { ProjectStructure } from "./ProjectStructure";
import {
  ProjectAccessDescription,
  ProjectControlsPanel,
  ProjectDependenciesContainer,
  ToggleContainer,
} from "./styled";

export const ProjectAccess: FC = () => {
  const {
    resources: [projectName],
  } = useDialogInfo(true);

  const { t } = useTranslation("dialogs");
  const context = useContext();
  const [update, updateState] = updatePermissions.use();
  const closeDialog = useDialogReset();
  const currentUser: string = useSelector(getUsername);
  const { shadows, setScrollContainer } = useDialogShadow();
  const { users, usersAndRolesLoading, autoCompleteProps, pickedUsers, setPickedUsers } = useUsersAndRoles();
  const { projectStructure, loading: structureLoading } = useProjectInfo(projectName);
  const [activeUser, setActiveUser] = useState<string>("");
  const { alias: projectAlias } = projectStructure || {};
  const loading = structureLoading || usersAndRolesLoading;
  const [expanded, toggleExpanded] = useToggle(false);
  const [isRestricted, setIsRestricted] = useState<boolean>(false);

  useAccessNotifications(updateState);

  const {
    structure,
    partial,
    updateData,
    onBulkUpdate,
    onUpdateProject,
    onUserPermissionsInit,
    onUserPermissionsRemove,
    onUpdateEntity,
    enrichAclData,
    checkedAll,
  } = useStructure(projectStructure, activeUser, pickedUsers, loading);

  const { onUpdateTotal } = useTotal(onBulkUpdate, checkedAll, partial, activeUser);

  // Коллбэк, вызываемый при клике на кнопку "Сохранить".
  // Производим запрос на обновление acl вложенных сущностей, после чего
  // дергаем эндпоинт добавления сущностей в каталоги пользователей
  const onApply = useCallback(() => {
    if (!updateData) {
      return;
    }

    update({
      input: updateData,
    }).finally(closeDialog);
  }, [closeDialog, update, updateData]);

  // Проверка на то, присутствуют ли в структуре проекта сущности, для которых недоступно конфигурирование
  useEffect(() => {
    setIsRestricted(
      Boolean(
        structure?.tables.some(
          table => !isConfigure(table.permissions) || table.layers.some(layer => !isConfigure(layer.permissions)),
        ),
      ),
    );
  }, [structure]);

  // Обновление списка пользователей, которые необходимо отразить в списке чипов,
  // при изменении данных в структуре проекта (acl). При этом показываем чипы только для тех пользователей,
  // для которых в acl вложенных сущностей есть данные и при условии, что сущности эти пользователи
  // имеют право конфигурировать
  useEffect(() => {
    if (!structure || !users || !currentUser) {
      return;
    }

    let processedUsers = [];

    const checkEntityUser = (item: RolePermissionDc, entityPermissions: Permissions) => {
      const { role } = item;
      const userName = getNameByRole(role, users);

      if (processedUsers.includes(userName)) {
        return;
      }

      if (users.some(user => user.aclRole === role) && `__${currentUser}` !== role && isConfigure(entityPermissions)) {
        if (userName === AccessRoleGroup.Public) {
          processedUsers = [userName, ...processedUsers];
        } else {
          processedUsers = [...processedUsers, userName];
        }
      }
    };

    structure.acl.data.forEach(projectAclItem => {
      checkEntityUser(projectAclItem, structure.permissions);
    });

    structure.tables.forEach(table => {
      table.acl.data.forEach(tableAclItem => {
        checkEntityUser(tableAclItem, table.permissions);
      });

      table.layers.forEach(layer => {
        layer.acl.data.forEach(layerAclItem => {
          checkEntityUser(layerAclItem, layer.permissions);
        });
      });
    });

    setPickedUsers(processedUsers);
  }, [structure, users, currentUser]);

  const getTextName = useCallback((name: string) => (name === AccessRoleGroup.Public ? t("access.public") : name), [t]);
  const readOnly = activeUser === AccessRoleGroup.Public;

  return (
    <FullScreenDialog dialogContext={context}>
      <DialogTitle
        displayShadow={shadows.upper}
        titleText={t("access.dialogHeader")}
        style={{ paddingBottom: 0 }}
        extra={<Description style={{ marginTop: 0 }}>{projectAlias}</Description>}
      >
        <RestrictedWidthContainer>
          <ProjectAccessDescription>{t("access.accessDescription")}</ProjectAccessDescription>
          <ChipAutoComplete
            loading={loading}
            activeItem={activeUser}
            items={users as ChipAutoCompleteData[]}
            activeItems={pickedUsers}
            onSelectItem={(name: string) => {
              setActiveUser(name);
              onUserPermissionsInit(getAclRole(name, users));
            }}
            onRemoveItem={(name: string) => {
              setActiveUser("");
              onUserPermissionsRemove(getAclRole(name, users));
            }}
            onActiveItem={(name: string) => {
              setActiveUser(name);
              enrichAclData(getAclRole(name, users));
            }}
            slideButtonTitle={t("access.slide")}
            listButtonTitle={t("access.list")}
            placeholder={t("access.chooseUserName")}
            searchPlaceholder={t("access.search")}
            getTextName={getTextName}
            autoCompleteProps={autoCompleteProps}
          />
          {activeUser && (
            <>
              <CommonSettingsTitleContainer>
                <H3>{t("access.accessTitle")}</H3>
                <CommonSettingsControls>
                  <AccessControl
                    type="read"
                    active={checkedAll?.read || partial?.read}
                    clickable={!(checkedAll?.read || partial?.read)}
                    onClick={() => !(checkedAll?.read || partial?.read) && onUpdateTotal("read")}
                    partial={partial?.read}
                  />
                  {!readOnly && (
                    <>
                      <AccessControl
                        type="write"
                        active={checkedAll?.write || partial?.write}
                        onClick={() => onUpdateTotal("write")}
                        partial={partial?.write}
                      />
                      <AccessControl
                        type="configure"
                        active={checkedAll?.configure || partial?.configure}
                        onClick={() => onUpdateTotal("configure")}
                        partial={partial?.configure}
                      />
                    </>
                  )}
                </CommonSettingsControls>
                {isRestricted && <StructureMessage warning>{t("access.someResourcesUnavailable")}</StructureMessage>}
              </CommonSettingsTitleContainer>
              <ProjectControlsPanel>
                <ToggleContainer toggled={expanded} onClick={toggleExpanded}>
                  <Description>{t("access.extraSettings")}</Description>
                  <Icon kind={"expand"} />
                </ToggleContainer>
                <CopyPublicResourceLink
                  resourceType={ResourceType.Project}
                  isPublicRole={activeUser === AccessRoleGroup.Public}
                  name={projectName}
                />
              </ProjectControlsPanel>
            </>
          )}
        </RestrictedWidthContainer>
      </DialogTitle>
      <DialogContent ref={setScrollContainer} style={{ paddingTop: "1rem" }}>
        {activeUser && expanded && (
          <ProjectDependenciesContainer>
            <RestrictedWidthContainer>
              <ProjectStructure
                activeAclRole={getAclRole(activeUser, users)}
                structure={structure}
                readOnly={activeUser === AccessRoleGroup.Public}
                onUpdateProject={onUpdateProject}
                onUpdateEntity={onUpdateEntity}
              />
            </RestrictedWidthContainer>
          </ProjectDependenciesContainer>
        )}
      </DialogContent>
      <DialogActions displayShadow={shadows.bottom} isWaiting={loading || updateState.loading} onApply={onApply} />
    </FullScreenDialog>
  );
};
